/**
 * ffrm-lib.js — Framework-agnostic form helper for WebMenu checkout.
 *
 * No HMAC, no honeypot, no LPS coupling.
 * POSTs JSON to checkoutApiUrl. Stub mode simulates success when apiUrl is unset or "stub".
 *
 * Usage:
 *   Alpine.data('checkoutForm', FFrm.createAlpineComponent({ fields: [...] }))
 */
(function (global) {
  'use strict';

  var DEFAULT_REQUIRED = ['firstName', 'lastName', 'email', 'phone'];

  var FIELD_REGISTRY = {
    firstName: { type: 'text', inputType: 'text', placeholder: 'First Name', grid: 'half', testId: 'first-name' },
    lastName: { type: 'text', inputType: 'text', placeholder: 'Last Name', grid: 'half', testId: 'last-name' },
    email: { type: 'text', inputType: 'email', placeholder: 'Email', grid: 'full', testId: 'email' },
    phone: { type: 'text', inputType: 'tel', placeholder: 'Phone', grid: 'full', testId: 'phone' },
    partySize: {
      type: 'select',
      placeholder: 'Party size',
      grid: 'half',
      testId: 'party-size',
      options: [
        { value: '', label: 'Party size' },
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
        { value: '5', label: '5' },
        { value: '6', label: '6' },
        { value: '7', label: '7' },
        { value: '8', label: '8+' }
      ]
    },
    preferredDate: { type: 'date', placeholder: 'Preferred date', grid: 'half', testId: 'preferred-date' },
    specialInstructions: {
      type: 'textarea',
      placeholder: 'Special instructions (allergies, requests…)',
      grid: 'full',
      testId: 'special-instructions',
      rows: 3
    }
  };

  function defaultFields() {
    return ['firstName', 'lastName', 'phone', 'email', 'partySize', 'preferredDate', 'specialInstructions'];
  }

  function resolveFormConfig(opts) {
    var cfg = global._webMenuConfig || {};
    var fields = opts.fields || cfg.formFields || defaultFields();
    if (fields.indexOf('orderDetails') === -1) {
      fields = fields.concat(['orderDetails']);
    }
    var required = opts.requiredFields || cfg.requiredFields || DEFAULT_REQUIRED.slice();
    required = required.filter(function (f) { return fields.indexOf(f) !== -1; });
    return { fields: fields, required: required };
  }

  function emptyFormData(fields) {
    var data = {};
    fields.forEach(function (f) { data[f] = ''; });
    return data;
  }

  function buildVisibleFields(fields) {
    return fields
      .filter(function (f) { return f !== 'orderDetails' && FIELD_REGISTRY[f]; })
      .map(function (f) {
        return Object.assign({ key: f }, FIELD_REGISTRY[f]);
      });
  }

  function getApiUrl(opts) {
    var cfg = global._webMenuConfig || {};
    return opts.apiUrl || cfg.checkoutApiUrl || '';
  }

  function isStubMode(apiUrl) {
    return !apiUrl || apiUrl === 'stub' || apiUrl === '/stub' || apiUrl.indexOf('stub') !== -1;
  }

  function roundMoney(amount) {
    return Math.round((Number(amount) + Number.EPSILON) * 100) / 100;
  }

  function generateConfirmationNumber() {
    var now = new Date();
    var date = now.toISOString().slice(0, 10).replace(/-/g, '');
    var time = String(now.getHours()).padStart(2, '0') +
      String(now.getMinutes()).padStart(2, '0') +
      String(now.getSeconds()).padStart(2, '0') +
      String(now.getMilliseconds()).padStart(3, '0');
    var rand = '';
    if (global.crypto && global.crypto.getRandomValues) {
      var bytes = new Uint8Array(4);
      global.crypto.getRandomValues(bytes);
      rand = Array.from(bytes).map(function (b) { return b.toString(16).padStart(2, '0'); }).join('').toUpperCase();
    } else {
      rand = Math.random().toString(36).slice(2, 10).toUpperCase();
    }
    return 'WM-' + date + '-' + time + '-' + rand;
  }

  function buildPayload(formData, cart, confirmationNumber) {
    var cfg = global._webMenuConfig || {};
    var items = cart || [];
    var customer = {};
    Object.keys(formData).forEach(function (key) {
      if (key === 'orderDetails' || key === 'specialInstructions') return;
      customer[key] = formData[key];
    });

    return {
      pageId: cfg.pageId || null,
      businessName: cfg.businessName || null,
      submittedAt: new Date().toISOString(),
      confirmationNumber: confirmationNumber,
      customer: customer,
      orderDetails: formData.orderDetails,
      specialInstructions: formData.specialInstructions || '',
      cart: items.map(function (i) {
        return { id: i.id, name: i.name, price: i.price, quantity: i.quantity };
      }),
      cartTotal: roundMoney(items.reduce(function (s, i) { return s + i.price * i.quantity; }, 0))
    };
  }

  function buildReceipt(formData, cart, confirmationNumber) {
    var items = (cart || []).map(function (i) {
      return {
        id: i.id,
        name: i.name,
        price: i.price,
        quantity: i.quantity,
        lineTotal: roundMoney(i.price * i.quantity)
      };
    });
    return {
      confirmationNumber: confirmationNumber,
      items: items,
      total: roundMoney(items.reduce(function (s, i) { return s + i.lineTotal; }, 0)),
      specialInstructions: formData.specialInstructions || '',
      customer: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        partySize: formData.partySize,
        preferredDate: formData.preferredDate
      }
    };
  }

  async function postToApi(apiUrl, payload) {
    var res = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      var text = await res.text().catch(function () { return res.statusText; });
      throw new Error('API ' + res.status + ': ' + text);
    }
    return res.json().catch(function () { return { ok: true }; });
  }

  async function stubSubmit(payload) {
    await new Promise(function (r) { setTimeout(r, 400); });
    console.info('[FFrm stub] Would POST to your API:', payload);
    if (global._webMenuConfig && global._webMenuConfig.onStubSubmit) {
      global._webMenuConfig.onStubSubmit(payload);
    }
    return {
      ok: true,
      stub: true,
      reference: payload.confirmationNumber
    };
  }

  var FFrm = {
    FIELD_REGISTRY: FIELD_REGISTRY,
    DEFAULT_REQUIRED: DEFAULT_REQUIRED,
    buildVisibleFields: buildVisibleFields,
    generateConfirmationNumber: generateConfirmationNumber,

    createAlpineComponent: function (opts) {
      opts = opts || {};
      var formConfig = resolveFormConfig(opts);
      var fields = formConfig.fields;
      var requiredFields = formConfig.required;

      return function () {
        return {
          formData: emptyFormData(fields),
          visibleFields: buildVisibleFields(fields),
          requiredFields: requiredFields,
          submitting: false,
          submitted: false,
          error: null,
          reference: null,
          receipt: null,

          isFieldRequired: function (key) {
            return this.requiredFields.indexOf(key) !== -1;
          },

          syncSpecialInstructionsToCart: function () {
            if (!global.Alpine || !global.Alpine.store('cart')) return;
            global.Alpine.store('cart').setSpecialInstructions(this.formData.specialInstructions || '');
          },

          syncCartToForm: function () {
            if (!global.Alpine || !global.Alpine.store('cart')) return;
            var cart = global.Alpine.store('cart');
            var items = cart.items;
            this.formData.orderDetails = items
              .map(function (i) { return i.name + ' (x' + i.quantity + ')'; })
              .join(', ');
            this.formData.specialInstructions = cart.specialInstructions || this.formData.specialInstructions || '';
          },

          init: function () {
            var self = this;
            this.$watch('formData.specialInstructions', function () {
              self.syncSpecialInstructionsToCart();
            });
          },

          submit: async function () {
            this.error = null;
            this.submitting = true;
            this.syncCartToForm();

            var apiUrl = getApiUrl(opts);
            var cartStore = global.Alpine ? global.Alpine.store('cart') : null;
            var cartItems = cartStore ? cartStore.items : [];
            var confirmationNumber = generateConfirmationNumber();
            var payload = buildPayload(this.formData, cartItems, confirmationNumber);

            try {
              var result;
              if (isStubMode(apiUrl)) {
                result = await stubSubmit(payload);
              } else {
                result = await postToApi(apiUrl, payload);
              }
              var ref = result.reference || result.confirmationNumber || confirmationNumber;
              this.receipt = buildReceipt(this.formData, cartItems, ref);
              this.reference = ref;
              this.submitted = true;
              if (cartStore) {
                cartStore.clear();
              }
            } catch (e) {
              this.error = e.message || 'Submission failed. Please try again.';
            } finally {
              this.submitting = false;
            }
          }
        };
      };
    }
  };

  global.FFrm = FFrm;
})(window);
